/**
 * Utility functions for handling notification navigation
 * Maps notification types and entity types to their corresponding routes
 */

import { Notification } from '@/services/notificationService';

export interface NotificationNavigationResult {
	path: string;
	handled: boolean;
}

/**
 * Get the navigation path for a notification based on its type and data
 */
export const getNotificationNavigationPath = (notification: Notification): NotificationNavigationResult => {
	const data = notification.data || {};
	const entityType = data.entity_type;
	const entityId = data.entity_id;

	// Handle comment mentions - navigate to the entity with focus on comments
	if (notification.type === 'comment_mention') {
		switch (entityType) {
			case 'candidate':
				return {
					path: `/candidates/${entityId}?tab=comments&highlight=${data.comment_id}`,
					handled: true
				};
			case 'job':
				return {
					path: `/jobs/${entityId}?tab=comments&highlight=${data.comment_id}`,
					handled: true
				};
			case 'company':
				return {
					path: `/companies/${entityId}?tab=comments&highlight=${data.comment_id}`,
					handled: true
				};
			case 'match':
				return {
					path: `/matches/${entityId}?tab=comments&highlight=${data.comment_id}`,
					handled: true
				};
			case 'task':
				return {
					path: `/tasks?task=${entityId}`,
					handled: true
				};
			default:
				return { path: '/notifications', handled: false };
		}
	}

	// Handle task-related notifications (assignment, mentions in description)
	if (notification.type === 'task_assigned' || notification.type === 'task_reporter' || entityType === 'task') {
		if (entityId || data.task_id) {
			return {
				path: `/tasks?task=${entityId || data.task_id}`,
				handled: true
			};
		}
	}

	// Handle job-related notifications
	if (notification.type === 'job_update' || notification.type === 'new_application' || notification.type === 'application_status_change') {
		if (entityId) {
			return {
				path: `/jobs/${entityId}`,
				handled: true
			};
		}
	}

	// Handle candidate-related notifications
	if (notification.type === 'candidate_update') {
		if (entityId) {
			return {
				path: `/candidates/${entityId}`,
				handled: true
			};
		}
	}

	// Handle company-related notifications
	if (data.entity_type === 'company' && entityId) {
		return {
			path: `/companies/${entityId}`,
			handled: true
		};
	}

	// Handle match-related notifications
	if (data.entity_type === 'match' && entityId) {
		return {
			path: `/matches/${entityId}`,
			handled: true
		};
	}

	// Handle AI processing complete notifications
	if (notification.type === 'ai_processing_complete') {
		switch (entityType) {
			case 'candidate':
				return {
					path: `/candidates/${entityId}?tab=analysis`,
					handled: true
				};
			case 'job':
				return {
					path: `/jobs/${entityId}?tab=analysis`,
					handled: true
				};
			default:
				if (entityId) {
					return {
						path: `/${entityType}s/${entityId}`,
						handled: true
					};
				}
		}
	}

	// Default: navigate to notifications page
	return {
		path: '/notifications',
		handled: false
	};
};
